import { useAppDispatch } from '../../../app/hooks';
import { useGetProductsQuery } from '../../../features/shared/productsApi';
import Switch from "react-switch";
import { useEffect, useState } from 'react';
import { toggleSideNav } from '../../../app/slice/dashboardSlice';
import { Menu, Info, X } from 'lucide-react';

interface IDashboardComponentWrapper {
  children: JSX.Element;
  title?: string;
  subTitle?: string,
  pageDetails?: string,
  overrideName?: () => JSX.Element;
  renderNavItems?: () => JSX.Element;
}

const helpText: Record<string, string> = {
  // 'Payments': 'Manage all payments',
  // 'Beneficiaries': 'Manage all you beneficaries.',
  // 'Custodians': 'Manage all custodians'
}

const DashboardComponentWrapper = ({ children, title, subTitle, pageDetails, renderNavItems, overrideName }: IDashboardComponentWrapper) => {
  const dispatch = useAppDispatch();
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const seen = title && localStorage.getItem(title);
    if (!seen && title) {
      setShowHelp(true);
      localStorage.setItem(title, 'true');
    }
  }, [title]);

  const handleCloseText = () => {
    title && localStorage.setItem(title, 'true');
    setShowHelp(false);
  };

  return (
    <div className='flex flex-col min-h-[100dvh] w-full bg-neutral-50/50'>
      
      {/* Mobile Top Header (Visible on mobile/tablet only) */}
      <div className='lg:hidden flex items-center justify-between px-4 sm:px-6 h-16 bg-white border-b border-neutral-200 sticky top-0 z-40 shadow-sm'>
        <button 
          onClick={() => dispatch(toggleSideNav(true))}
          className='p-2 -ml-2 text-neutral-600 hover:text-neutral-900 rounded-md hover:bg-neutral-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#0898a0]/20'
          aria-label="Toggle Navigation"
        >
          <Menu size={24} />
        </button>
        
        <div className='flex items-center gap-3'>
          {overrideName ? overrideName() : null}
        </div>
      </div>

      {/* Main Content Container */}
      <div className='flex-1 flex flex-col w-full mx-auto relative'>
        
        {/* Page Header Area */}
        {(title || subTitle || pageDetails || renderNavItems) && (
          <div className='px-4 sm:px-6 lg:px-8 py-6 md:py-8 flex flex-col xl:flex-row xl:items-end justify-between gap-6'>
            
            {/* Title & Description Block */}
            <div className='flex flex-col gap-1.5 w-full max-w-4xl !text-start'>
              <div className='flex items-center gap-3 flex-wrap'>
                {title && (
                  <h1 className='text-2xl sm:text-[28px] leading-tight font-bold text-neutral-900 tracking-tight !text-start'>
                    {title}
                  </h1>
                )}
                
                {/* Help Popover */}
                {title && helpText[title] && (
                  <div className='relative flex items-center'>
                    <button 
                      onClick={() => setShowHelp(!showHelp)}
                      className='p-1.5 rounded-full text-neutral-400 hover:text-[#0898a0] hover:bg-[#0898a0]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#0898a0]/20'
                    >
                      <Info size={18} />
                    </button>
                    
                    {showHelp && (
                      <div className='absolute left-0 top-full mt-2 w-64 sm:w-72 bg-white p-4 rounded-xl shadow-xl border border-neutral-100 z-50 animate-in fade-in slide-in-from-top-2'>
                        <button 
                          onClick={handleCloseText}
                          className='absolute top-2 right-2 p-1.5 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-50 transition-colors'
                        >
                          <X size={14} />
                        </button>
                        <div className='pr-6'>
                          <p className='text-sm text-neutral-600 font-medium leading-relaxed'>
                            {helpText[title]}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {subTitle && (
                <h2 className='text-lg font-medium text-neutral-600 mt-1 !text-start'>
                  {subTitle}
                </h2>
              )}
              
              {pageDetails && (
                <p className='text-[13px] sm:text-sm text-neutral-500 max-w-2xl mt-1 leading-relaxed !text-start'>
                  {pageDetails}
                </p>
              )}
            </div>

            {/* Action / Nav Items Block */}
            {renderNavItems && (
              <div className='flex-shrink-0 flex items-center gap-3 flex-wrap xl:justify-end mt-2 xl:mt-0'>
                {renderNavItems()}
              </div>
            )}
          </div>
        )}

        {/* Dynamic Children / Content Area */}
        <div className={`flex-1 flex flex-col w-full h-full px-4 sm:px-6 lg:px-8 pb-8 ${(title || subTitle || renderNavItems) ? '' : 'pt-6 lg:pt-8'}`}>
           {children}
        </div>

      </div>
    </div>
  );
};

export default DashboardComponentWrapper;

export const SwitchMode = () => {
  const { data: productResponse } = useGetProductsQuery();
  const productData = productResponse?.data || {};

  const handleSwitchMode = () => {
    // Keep placeholder functionality from legacy
  }

  return (
    <div className="flex items-center gap-2">
      <span className='text-sm font-bold text-neutral-700'>Go live</span>
      <Switch
        onChange={handleSwitchMode}
        checked={productData.status === 'Production'}
        onColor="#0898a0"
        offColor='#e5e7eb'
        onHandleColor="#ffffff"
        handleDiameter={18}
        uncheckedIcon={false}
        checkedIcon={false}
        height={22}
        width={42}
        className="react-switch hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-[#0898a0]/20 rounded-full"
        id="material-switch"
      />
    </div>
  );
};
